/**
 * @file The share-link gate in front of every send that carries attachment links.
 * Consumed by `FormEmailModal.handleSend` and by `WorkflowParameterContent`'s
 * run pre-flight, both of which call `ensureFilesShared` before dispatching.
 *
 * `createAttachmentLink` refuses some versions per file — a chunk-stored version
 * has no shareable whole object — and reports that refusal only inside
 * `results[fileId:versionId].error`, with no top-level batch error, while
 * `shareMultipleVersions` returns `null` once every file failed. Either way the
 * file comes back without a `shareUrl` and `prepareEmailBody` silently drops it,
 * so this hook raises its own toast and throws rather than returning files the
 * caller would mail with no link (SYD-2807).
 */
import { CreateAttachmentLinkBatchResult } from '../../../../services/createAttachmentLinkService';
import { ShareableFile } from '../../../selectors/FileLinksSelector';

interface UseFileSharingParams {
  shareMultipleVersions: (requests: Array<{
    fileId: string;
    versionId: string;
    fileName: string;
  }>) => Promise<CreateAttachmentLinkBatchResult | null>;
  onError: (message: string) => void;
  onLoading: (message: string) => void;
}

/**
 * Hook for managing file sharing operations
 * @param shareMultipleVersions - Function to share multiple file versions
 * @param onError - Error callback
 * @param onLoading - Loading callback
 * @returns Object with ensureFilesShared function
 */
export const useFileSharing = ({ shareMultipleVersions, onError, onLoading }: UseFileSharingParams) => {
  /**
   * Ensures all selected files have share links, creating them if needed
   * @param selectedFiles - Array of files to ensure are shared
   * @returns The same files, each carrying a share URL
   * @throws When any selected file has no share link, naming those files
   */
  const ensureFilesShared = async (selectedFiles: ShareableFile[]): Promise<ShareableFile[]> => {
    if (selectedFiles.length === 0) {
      return selectedFiles;
    }

    const filesToShare = selectedFiles.filter(file => !file.isShared || !file.shareUrl);

    if (filesToShare.length === 0) {
      return selectedFiles;
    }

    onLoading('Creating share links for attachments...');

    const sharingRequests = filesToShare.map(file => ({
      fileId: file.fileId,
      versionId: file.versionId,
      fileName: file.fileName
    }));

    const shareResult = await shareMultipleVersions(sharingRequests);

    if (shareResult?.error) {
      onError(`Failed to create share links: ${shareResult.error}`);
      throw new Error(shareResult.error);
    }

    const sharedFiles = selectedFiles.map(file => {
      if (file.isShared && file.shareUrl) return file;

      const result = shareResult?.results?.[`${file.fileId}:${file.versionId}`];

      if (result && !result.error && result.shareUrl) {
        return {
          ...file,
          shareUrl: result.shareUrl,
          isShared: true
        };
      }

      return file;
    });

    const unlinkedFiles = sharedFiles.filter(file => !file.isShared || !file.shareUrl);

    if (unlinkedFiles.length > 0) {
      const message = `No share link could be created for ${unlinkedFiles.map(file => file.fileName).join(', ')}. Nothing was sent.`;
      onError(message);
      throw new Error(message);
    }

    return sharedFiles;
  };

  return { ensureFilesShared };
};
